"use client";
import React, { useCallback, useEffect, useRef } from 'react';
import { BubbleMenu, InspectionOverlay } from './components';
import {
  useDarkMode,
  useElementBubblePosition,
  useElementSelection,
  useIframeMessaging,
  useInspector
} from './hooks';
import { UI_CONSTANTS } from './constants';

// Function to send DOM structure to parent window
export const sendDomStructure = () => {
  // Just send the entire HTML body content as a string
  const bodyHTML = document.body.outerHTML;
  window.parent.postMessage({ 
    type: 'DOM_STRUCTURE_UPDATED', 
    payload: bodyHTML 
  }, '*');
};

// Function to setup DOM observer
export const setupDomObserver = (callback: () => void) => {
  let debounceTimeout: number | undefined;
  
  const observer = new MutationObserver(() => {
    // Debounce DOM updates to avoid performance issues
    if (debounceTimeout) window.clearTimeout(debounceTimeout);
    debounceTimeout = window.setTimeout(() => {
      callback();
    }, 500); // Wait 500ms after changes stop
  });
  
  // Start observing with configuration
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    characterData: false
  });
  
  return {
    disconnect: () => {
      if (debounceTimeout) window.clearTimeout(debounceTimeout);
      observer.disconnect();
    }
  };
};

export interface ElementInspectorProps {
  /**
   * Initial state of the inspector (active or not)
   */
  initialIsActive?: boolean;
  
  /**
   * CSS selector for elements to exclude from selection
   */
  excludeSelector?: string;
  
  /**
   * Custom label for selected elements
   */
  elementLabel?: (element: HTMLElement) => React.ReactNode;
  
  /**
   * Custom styles for the selector
   */
  selectorStyle?: React.CSSProperties;
  
  /**
   * Custom styles for the highlighter
   */
  highlighterStyle?: React.CSSProperties;
  
  /**
   * Whether to show the floating bubble menu button
   * @default false
   */
  showBubbleMenuButton?: boolean;
}

/**
 * A component that allows inspecting and selecting DOM elements with a UI for interaction
 */
export function ElementInspector({
  initialIsActive = true,
  excludeSelector = '.element-inspector-bubble, .element-inspector-controls, .element-inspector-mention',
  elementLabel,
  selectorStyle,
  highlighterStyle,
  showBubbleMenuButton = false,
}: ElementInspectorProps) {
  // Custom hooks
  const isDarkMode = useDarkMode();
  const {
    sendSelectedElements, 
    sendPrompt,
    shouldEnableInspect,
  } = useIframeMessaging();
  
  // Element selection hook
  const { 
    hoveredElement, 
    selectedElements, 
    handleElementHovered, 
    handleElementUnhovered, 
    handleElementSelected,
    clearSelections 
  } = useElementSelection({
    onElementsSelected: (elements) => {
      sendSelectedElements(elements);
    }
  });
  
  // Bubble position hook
  const { bubblePosition, isMenuAboveElement } = useElementBubblePosition({
    selectedElements,
    menuWidth: UI_CONSTANTS.MENU_WIDTH,
    spacing: UI_CONSTANTS.SPACING
  });
  
  // Inspector state hook
  const {
    isInspecting,
    userPrompt,
    setUserPrompt,
    toggleInspection,
    handlePromptSubmit,
  } =  useInspector({
    // Use shouldEnableInspect from iframe messaging if in iframe, otherwise use initialIsActive
    initialIsActive: shouldEnableInspect,
    onPromptGenerated: (prompt, elements) => {
      sendPrompt(prompt, elements);
    }
  });
  
  // Handle menu toggle - clear selections when toggling off
  const handleMenuToggle = useCallback(() => {
    toggleInspection();
    if (isInspecting) {
      clearSelections();
    }
  }, [isInspecting, toggleInspection, clearSelections]);
  
  // Handle form submission with selected elements
  const onSubmitPrompt = useCallback((e: React.FormEvent) => {
    handlePromptSubmit(e, selectedElements);
  }, [handlePromptSubmit, selectedElements]);
  
  // Update inspection state when shouldEnableInspect changes (from iframe messages)
  useEffect(() => {
    if (isInspecting !== shouldEnableInspect) {
      toggleInspection();
    }
  }, [isInspecting, shouldEnableInspect, toggleInspection]);

  // Reference to store observer
  const observerRef = useRef<{ disconnect: () => void } | null>(null);
  
  // Send ready message to parent window when component mounts
  useEffect(() => {
    // Send UI builder ready message
    window.parent.postMessage({ type: 'UI_BUILDER_READY' }, '*');
    
    // Send initial DOM structure
    sendDomStructure();
    
    // Set up observer for DOM changes
    observerRef.current = setupDomObserver(sendDomStructure);
    
    // Clean up observer when component unmounts
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return (
    <div>
      {/* Element Inspector Components */}
      <InspectionOverlay
        isInspecting={isInspecting}
        hoveredElement={hoveredElement}
        selectedElements={selectedElements}
        onElementHovered={handleElementHovered}
        onElementSelected={handleElementSelected}
        onElementUnhovered={handleElementUnhovered}
        excludeSelector={excludeSelector}
        selectorStyle={selectorStyle}
        highlighterStyle={highlighterStyle}
        elementLabel={elementLabel}
      />

      {/* Bubble Menu */}
      <BubbleMenu
        isInspecting={isInspecting}
        selectedElements={selectedElements}
        bubblePosition={bubblePosition}
        isMenuAboveElement={isMenuAboveElement}
        userPrompt={userPrompt}
        setUserPrompt={setUserPrompt}
        onSubmitPrompt={onSubmitPrompt}
        onToggleInspection={handleMenuToggle}
        isDarkMode={isDarkMode}
        showBubbleMenuButton={showBubbleMenuButton}
      />
    </div>
  );
}
